/**
 * Theme switch: Light / Dark / System (CLAUDE.md 14.2).
 *
 * Three states, not a binary. "System" is the default and must stay reachable --
 * a two-way toggle silently opts the user out of following their OS at sunset.
 *
 * Rendered as a Radix dropdown rather than a cycling button so the current choice is
 * visible without clicking, and so it is operable by keyboard and screen reader.
 */
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Check, Laptop, Moon, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/cn'
import { useTheme, type ThemeChoice } from '@/theme/ThemeProvider'

const OPTIONS: { value: ThemeChoice; icon: typeof Sun; labelKey: string }[] = [
  { value: 'light', icon: Sun, labelKey: 'theme.light' },
  { value: 'dark', icon: Moon, labelKey: 'theme.dark' },
  { value: 'system', icon: Laptop, labelKey: 'theme.system' },
]

export function ThemeToggle({ className }: { className?: string }) {
  const { t } = useTranslation()
  const { choice, resolved, setTheme } = useTheme()

  // The trigger shows what is *in effect*, which for "system" is the resolved theme --
  // that is the question a user glancing at the icon is actually asking.
  const TriggerIcon = choice === 'system' ? Laptop : resolved === 'dark' ? Moon : Sun

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={t('theme.label')}
        className={cn(
          'flex h-11 w-11 items-center justify-center rounded-md text-text-muted',
          'hover:bg-surface-raised hover:text-text',
          className,
        )}
      >
        <TriggerIcon size={18} aria-hidden />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-[10rem] rounded-md border border-border bg-surface-raised p-1 shadow-[var(--shadow-md)]"
        >
          {OPTIONS.map(({ value, icon: Icon, labelKey }) => (
            <DropdownMenu.Item
              key={value}
              onSelect={() => setTheme(value)}
              className={cn(
                'flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm outline-none',
                'data-[highlighted]:bg-surface data-[highlighted]:text-text',
              )}
            >
              <Icon size={15} aria-hidden />
              <span className="flex-1">{t(labelKey)}</span>
              {choice === value && <Check size={14} aria-hidden />}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
